/**
 * In-app purchases — MOCK implementation for the web prototype.
 *
 * No money moves: `purchase()` waits ~600 ms and records a fake receipt in
 * local storage. The UI must label every purchase "Test purchase — no money is
 * charged" while `testMode` is true, and callers must pass the parental gate
 * before calling purchase() or restore().
 *
 * Policy (docs/MONETIZATION.md): nothing sold here changes a ranked run.
 * - remove_ads: hides banner slots (one-time).
 * - Dropstack Plus (monthly / yearly): no ads while active. Practice packs,
 *   bonus cosmetic themes and a parent report are planned; their hooks are
 *   the PlusFeatures stubs in services/plusFeatures.ts ("coming soon" in the
 *   store until built).
 *
 * Swapping in a real store (StoreKit 2, Google Play Billing, RevenueCat) means
 * replacing createPurchaseService() with an adapter implementing the same
 * PurchaseService contract; see docs/MONETIZATION.md § Integration points.
 */
import { useCallback, useSyncExternalStore } from 'react';
import type { Entitlements, Product, ProductId, PurchaseService } from '../contracts.ts';
import { getAppStorage, isFiniteNumber, isRecord, STORAGE_KEYS, type JsonStore } from '../state/storage.ts';

export interface CatalogProduct extends Product {
  /** Billing period for subscriptions; null for one-time products. */
  period: 'month' | 'year' | null;
  /** Price in South African cents (the label is what the UI shows). */
  priceCents: number;
}

/** Prices are shown plainly, exactly as listed — no strike-through "was" prices, no countdowns. */
export const PRODUCT_CATALOG: readonly CatalogProduct[] = [
  { id: 'remove_ads', kind: 'one-time', priceLabel: 'R49', period: null, priceCents: 4900 },
  { id: 'plus_monthly', kind: 'subscription', priceLabel: 'R29', period: 'month', priceCents: 2900 },
  { id: 'plus_yearly', kind: 'subscription', priceLabel: 'R249', period: 'year', priceCents: 24900 },
];

export const NO_ENTITLEMENTS: Readonly<Entitlements> = Object.freeze({ adsRemoved: false, plus: false, plusExpiresAt: null });

/** Simulated store latency. */
export const MOCK_PURCHASE_DELAY_MS = 600;

const DAY_MS = 24 * 60 * 60 * 1000;
const PERIOD_MS: Record<'month' | 'year', number> = { month: 30 * DAY_MS, year: 365 * DAY_MS };

export type PurchaseOutcome = Awaited<ReturnType<PurchaseService['purchase']>>;

export interface PurchaseServiceOptions {
  store?: JsonStore;
  /** Simulated store latency (default 600 ms; tests pass 0). */
  delayMs?: number;
  /** Clock for receipts and subscription expiry (default Date.now). */
  now?: () => number;
  /** QA hook: decide how a mock purchase ends (default: always 'purchased'). */
  outcome?: (id: ProductId) => PurchaseOutcome;
}

/** The mock service also says it is a test store, so the UI can label purchases. */
export interface MockPurchaseService extends PurchaseService {
  readonly testMode: true;
}

/** A fake store receipt — the source of truth that restore() re-reads. */
interface Receipt {
  productId: ProductId;
  purchasedAt: number;
  /** Subscription end (ms); null for one-time products. */
  expiresAt: number | null;
}

export function createPurchaseService(options: PurchaseServiceOptions = {}): MockPurchaseService {
  const store = options.store ?? getAppStorage();
  const delayMs = options.delayMs ?? MOCK_PURCHASE_DELAY_MS;
  const now = options.now ?? Date.now;
  const outcome = options.outcome ?? (() => 'purchased');
  const listeners = new Set<(e: Entitlements) => void>();

  let receipts = loadReceipts(store);
  let current: Entitlements = deriveEntitlements(receipts, now());
  let pending = false;

  const notify = () => {
    for (const listener of [...listeners]) listener(current);
  };

  /**
   * Recompute (subscriptions expire with time) and notify when anything
   * changed. `deferNotify` is for reads: getEntitlements() is used as a React
   * useSyncExternalStore snapshot, and notifying synchronously from inside a
   * render would update other components mid-render.
   */
  const refresh = (deferNotify = false): Entitlements => {
    const next = deriveEntitlements(receipts, now());
    if (!sameEntitlements(next, current)) {
      current = next;
      if (deferNotify) queueMicrotask(notify);
      else notify();
    }
    return current;
  };

  return {
    testMode: true,

    async getProducts() {
      return PRODUCT_CATALOG.map((p) => ({ ...p }));
    },

    async purchase(id) {
      const product = PRODUCT_CATALOG.find((p) => p.id === id);
      if (!product || pending) return 'failed';
      pending = true;
      try {
        await wait(delayMs);
        const result = outcome(id);
        if (result !== 'purchased') return result;
        // A one-time product is owned once; buying it again is a no-op, as in the real stores.
        if (!product.period && receipts.some((r) => r.productId === id)) return 'purchased';
        const at = now();
        // A subscription extends from the later of now and the current Plus expiry, like a renewal.
        const plusUntil = Math.max(at, ...receipts.map((r) => r.expiresAt ?? 0));
        const expiresAt = product.period ? plusUntil + PERIOD_MS[product.period] : null;
        receipts = [...receipts, { productId: id, purchasedAt: at, expiresAt }];
        store.set(STORAGE_KEYS.purchases, { receipts });
        refresh();
        return 'purchased';
      } finally {
        pending = false;
      }
    },

    async restore() {
      await wait(delayMs);
      receipts = loadReceipts(store);
      return refresh();
    },

    getEntitlements: () => refresh(true),

    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

let appPurchases: MockPurchaseService | null = null;

/**
 * The app-wide purchase service (Settings, the ads service and the paywall
 * share one, so a purchase anywhere updates entitlements everywhere).
 */
export function getPurchaseService(): MockPurchaseService {
  appPurchases ??= createPurchaseService();
  return appPurchases;
}

/**
 * React hook: current entitlements, re-rendering when a purchase, restore or
 * subscription expiry changes them.
 *
 *   const { adsRemoved, plus } = useEntitlements();
 */
export function useEntitlements(service: PurchaseService = getPurchaseService()): Entitlements {
  // Wrapped so class-based adapters (StoreKit/Play Billing/RevenueCat) that rely on `this` work too.
  const subscribe = useCallback((onChange: () => void) => service.subscribe(onChange), [service]);
  const snapshot = useCallback(() => service.getEntitlements(), [service]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Entitlements implied by a set of receipts at time `at`. */
export function deriveEntitlements(receipts: readonly Receipt[], at: number): Entitlements {
  const activePlus = receipts.filter((r) => r.expiresAt !== null && r.expiresAt > at);
  const plusExpiry = activePlus.reduce<number | null>((max, r) => Math.max(max ?? 0, r.expiresAt ?? 0), null);
  const plus = plusExpiry !== null;
  return {
    adsRemoved: plus || receipts.some((r) => r.productId === 'remove_ads'),
    plus,
    plusExpiresAt: plusExpiry === null ? null : new Date(plusExpiry).toISOString(),
  };
}

function sameEntitlements(a: Entitlements, b: Entitlements): boolean {
  return a.adsRemoved === b.adsRemoved && a.plus === b.plus && a.plusExpiresAt === b.plusExpiresAt;
}

function loadReceipts(store: JsonStore): Receipt[] {
  return store.get(STORAGE_KEYS.purchases, decodeLedger) ?? [];
}

function decodeLedger(raw: unknown): Receipt[] | null {
  if (!isRecord(raw) || !Array.isArray(raw.receipts)) return null;
  return raw.receipts.flatMap((r): Receipt[] => {
    if (!isRecord(r)) return [];
    const product = PRODUCT_CATALOG.find((p) => p.id === r.productId);
    if (!product || !isFiniteNumber(r.purchasedAt)) return [];
    const expiresAt = isFiniteNumber(r.expiresAt) ? r.expiresAt : null;
    if (product.period && expiresAt === null) return [];
    return [{ productId: product.id, purchasedAt: r.purchasedAt, expiresAt: product.period ? expiresAt : null }];
  });
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
